import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { academyApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { TourOverlay } from '@/components/academy/TourOverlay';

const AcademyContext = createContext(null);
const TOUR_KEY = 'va_active_tour';

const readTour = () => {
  try { return JSON.parse(sessionStorage.getItem(TOUR_KEY)); } catch { return null; }
};

export function AcademyProvider({ children }) {
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [tour, setTourState] = useState(readTour);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    Promise.all([academyApi.content(), academyApi.progress()])
      .then(async ([c, p]) => {
        setContent(c.data);
        let done = p.data.completed;
        const auto = c.data.checklist.find(i => i.auto);
        if (auto && !done.includes(auto.id)) {
          done = (await academyApi.setProgress(auto.id, true)).data.completed;
        }
        setCompleted(done);
      })
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const setTour = useCallback((t) => {
    if (t) sessionStorage.setItem(TOUR_KEY, JSON.stringify(t));
    else sessionStorage.removeItem(TOUR_KEY);
    setTourState(t);
  }, []);

  const toggle = useCallback(async (id, done) => {
    setCompleted(prev => done ? [...new Set([...prev, id])] : prev.filter(x => x !== id));
    try {
      const res = await academyApi.setProgress(id, done);
      setCompleted(res.data.completed);
    } catch {
      toast.error('Kunde inte spara checklistan');
    }
  }, []);

  const startTour = useCallback((id) => setTour({ id, step: 0 }), [setTour]);

  const finishTour = useCallback((completedTour) => {
    if (completedTour && content) {
      const item = content.checklist.find(i => i.tour === tour?.id);
      if (item) toggle(item.id, true);
      toast.success('Guiden är klar!');
    }
    setTour(null);
  }, [content, tour, toggle, setTour]);

  const openPdf = useCallback(async (slug, download = false) => {
    const win = download ? null : window.open('', '_blank');
    try {
      const res = await academyApi.pdf(slug, download);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        const ver = content?.documents.find(d => d.slug === slug)?.version;
        a.download = `Vapelarm_${slug}${ver ? `_v${ver}` : ''}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (win) win.close();
      toast.error('Kunde inte öppna dokumentet');
    }
  }, [content]);

  const value = useMemo(() => ({ content, completed, toggle, startTour, openPdf, loadError, reload: load }),
    [content, completed, toggle, startTour, openPdf, loadError, load]);

  return (
    <AcademyContext.Provider value={value}>
      {children}
      {tour && content?.tours?.[tour.id] && (
        <TourOverlay
          tour={content.tours[tour.id]}
          step={tour.step}
          onStep={(step) => setTour({ ...tour, step })}
          onClose={finishTour}
        />
      )}
    </AcademyContext.Provider>
  );
}

export const useAcademy = () => useContext(AcademyContext);
